const openOverlayEntries = [];
const inertedElements = new Map();
let originalBodyOverflow;
let originalDocumentOverflow;
let bodyObserver;

const isIsolationExempt = (element) =>
  element.matches?.("[data-ui-toast-root], [data-sonner-toaster]");

const restoreInert = (element, wasInert) => {
  element.inert = wasInert;
  if (!wasInert) element.removeAttribute("inert");
};

const syncOverlayIsolation = () => {
  const topOverlayRoot = openOverlayEntries.at(-1)?.root;
  const shouldBeInert = new Set();

  if (topOverlayRoot) {
    Array.from(document.body.children).forEach((element) => {
      if (element !== topOverlayRoot && !isIsolationExempt(element)) {
        shouldBeInert.add(element);
      }
    });
  }

  inertedElements.forEach((wasInert, element) => {
    if (!shouldBeInert.has(element)) {
      restoreInert(element, wasInert);
      inertedElements.delete(element);
    }
  });

  shouldBeInert.forEach((element) => {
    if (!inertedElements.has(element)) {
      inertedElements.set(element, Boolean(element.inert));
    }
    element.inert = true;
    element.setAttribute("inert", "");
  });

  if (topOverlayRoot) {
    if (originalBodyOverflow === undefined) {
      originalBodyOverflow = document.body.style.overflow;
      originalDocumentOverflow = document.documentElement.style.overflow;
    }
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
  } else if (originalBodyOverflow !== undefined) {
    document.body.style.overflow = originalBodyOverflow;
    document.documentElement.style.overflow = originalDocumentOverflow;
    originalBodyOverflow = undefined;
    originalDocumentOverflow = undefined;
  }
};

const startObservingBody = () => {
  if (bodyObserver || typeof MutationObserver === "undefined") return;
  bodyObserver = new MutationObserver(syncOverlayIsolation);
  bodyObserver.observe(document.body, { childList: true });
};

const stopObservingBody = () => {
  bodyObserver?.disconnect();
  bodyObserver = undefined;
};

const registerOverlayRoot = (root, restoreTarget) => {
  const existingIndex = openOverlayEntries.findIndex(
    (entry) => entry.root === root,
  );
  if (existingIndex >= 0) openOverlayEntries.splice(existingIndex, 1);
  openOverlayEntries.push({ root, restoreTarget });
  startObservingBody();
  syncOverlayIsolation();
};

const unregisterOverlayRoot = (root) => {
  const index = openOverlayEntries.findIndex((entry) => entry.root === root);
  if (index < 0) return null;

  const wasTop = index === openOverlayEntries.length - 1;
  const [entry] = openOverlayEntries.splice(index, 1);

  for (const higherEntry of openOverlayEntries.slice(index)) {
    if (
      !higherEntry.restoreTarget?.isConnected ||
      root.contains(higherEntry.restoreTarget)
    ) {
      higherEntry.restoreTarget = entry.restoreTarget;
    }
  }

  syncOverlayIsolation();
  if (openOverlayEntries.length === 0) stopObservingBody();
  return wasTop && entry.restoreTarget?.isConnected
    ? entry.restoreTarget
    : null;
};

const isTopOverlayRoot = (root) => openOverlayEntries.at(-1)?.root === root;
const hasOpenOverlayRoot = () => openOverlayEntries.length > 0;

const isPortalledOverlayTarget = (target, surface) =>
  target instanceof Element &&
  !surface.contains(target) &&
  Boolean(target.closest("[data-radix-popper-content-wrapper]"));

export {
  hasOpenOverlayRoot,
  isPortalledOverlayTarget,
  isTopOverlayRoot,
  registerOverlayRoot,
  unregisterOverlayRoot,
};
